import { loadStripe } from "@stripe/stripe-js";
import { useCartData } from "@/zustand/cartStore";

export class StripeApplePayProvider {
  constructor(publishableKey) {
    this.publishableKey = publishableKey;

    this.stripe = null;
    this.paymentRequest = null;
    this.canMakePaymentResult = false;
    this.processing = false;
    this.tokenHandler = null;
  }

  async initialize(product, country, currency) {
    if (!this.publishableKey) {
      throw new Error("Stripe publishable key is required");
    }

    const stripeInstance = await loadStripe(this.publishableKey);
    if (!stripeInstance) {
      throw new Error("Failed to load Stripe");
    }

    this.stripe = stripeInstance;
    this.label = product?.generic_name || "Order";

    const { cartTotal } = useCartData.getState();
    const amountInCents = Math.round(Number(cartTotal) * 100);

    const pr = stripeInstance.paymentRequest({
      country,
      currency: currency.toLowerCase(),
      total: {
        label: this.label,
        amount: amountInCents,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: "standard",
          label: "Standard Shipping",
          detail: "Delivery within 5-7 business days",
          amount: Math.round(parseFloat(Number(product?.shipping_price)) * 100),
        },
      ],
    });

    const canMake = await pr.canMakePayment();
    if (canMake && canMake.applePay) {
      this.canMakePaymentResult = true;
      this.paymentRequest = pr;

      pr.on("token", async (event) => {
        this.processing = true;

        try {
          if (this.tokenHandler) {
            const shippingAddress = event.shippingAddress;
            // A present-but-empty address is as unshippable as a missing one,
            // and we must not charge for an order we cannot deliver (DEV-5488).
            const street = (shippingAddress?.addressLine?.[0] || "").trim();
            const city = (shippingAddress?.city || "").trim();
            const country = (shippingAddress?.country || "").trim();
            if (!street || !city || !country) {
              // Throw only: the catch below completes the event and the finally
              // clears `processing`. Completing here as well would call
              // `event.complete` twice for one payment.
              throw new Error("A complete shipping address is required");
            }

            const applePayEvent = {
              token: { id: event.token.id },
              payerName: event.payerName || "",
              payerEmail: event.payerEmail || "",
              payerPhone: event.payerPhone || "",
              shippingAddress: {
                country: shippingAddress.country || "US",
                addressLine: [
                  (shippingAddress.addressLine && shippingAddress.addressLine[0]) || "",
                  (shippingAddress.addressLine && shippingAddress.addressLine[1]) || "",
                ],
                city: shippingAddress.city || "",
                region: shippingAddress.region || shippingAddress.city || "",
                postalCode: shippingAddress.postalCode || "",
              },
            };

            await this.tokenHandler(applePayEvent);
            event.complete("success");
          }
        } catch (error) {
          console.log("Stripe Apple Pay error:", error);
          event.complete("fail");
          throw error;
        } finally {
          this.processing = false;
        }
      });
    }
  }

  async canMakePayment() {
    return this.canMakePaymentResult;
  }

  async show() {
    if (!this.paymentRequest) {
      throw new Error("Payment request not initialized");
    }

    const { cartTotal } = useCartData.getState();
    if (!cartTotal || Number(cartTotal) <= 0) {
      throw new Error("Invalid cart total");
    }
    const amountInCents = Math.round(Number(cartTotal) * 100);
    this.paymentRequest.update({
      total: {
        label: this.label,
        amount: amountInCents,
      },
    });

    return this.paymentRequest.show();
  }

  onToken(handler) {
    this.tokenHandler = handler;
  }

  isProcessing() {
    return this.processing;
  }
}
